"use client";

import { bind, play, setEnabled, setVolume } from "cuelume";
import { Volume2, VolumeX } from "lucide-react";
import { usePathname } from "next/navigation";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { readStorage, writeStorage } from "@/lib/safe-storage";

const ENABLED_STORAGE_KEY = "tls.ui-sounds.enabled";
// v3 retires the v2 key on purpose: a `Number(null) === 0` bug used to persist
// a volume of 0 for every visitor, and the engine treats 0 as "skip playback".
// Volume has never been user-adjustable, so no real preference is discarded.
const VOLUME_STORAGE_KEY = "tls.ui-sounds.volume.v3";
// Cuelume's press/release recipes are intentionally brief and restrained.
// Keep the global multiplier high enough that they remain audible on ordinary
// laptop speakers without turning success/arrival cues into a dominant layer.
const DEFAULT_VOLUME = 0.65;

interface CuelumeContextValue {
  enabled: boolean;
  setEnabledPreference: (enabled: boolean) => void;
}

const CuelumeContext = createContext<CuelumeContextValue | null>(null);

function CuelumeNavigationCue() {
  const pathname = usePathname();
  const initialPathname = useRef(pathname);
  const hasInteracted = useRef(false);

  useEffect(() => {
    const markInteracted = () => {
      hasInteracted.current = true;
    };
    window.addEventListener("pointerdown", markInteracted, { passive: true });
    window.addEventListener("keydown", markInteracted, { passive: true });
    return () => {
      window.removeEventListener("pointerdown", markInteracted);
      window.removeEventListener("keydown", markInteracted);
    };
  }, []);

  useEffect(() => {
    if (initialPathname.current === pathname) return;
    initialPathname.current = pathname;
    if (hasInteracted.current) play("arrival", { volume: 0.45 });
  }, [pathname]);

  return null;
}

function readStoredEnabled() {
  return readStorage(ENABLED_STORAGE_KEY) !== "false";
}

function readStoredVolume() {
  // readStorage returns null for an absent key AND for unavailable storage,
  // and `Number(null)` is 0 — which passes a bare 0..1 range check and mutes
  // every cue. Treat missing (or blank) as "no preference stored".
  const raw = readStorage(VOLUME_STORAGE_KEY);
  if (raw === null || raw.trim() === "") return DEFAULT_VOLUME;
  const stored = Number(raw);
  return Number.isFinite(stored) && stored >= 0 && stored <= 1 ? stored : DEFAULT_VOLUME;
}

export function CuelumeProvider({ children }: { children: React.ReactNode }) {
  const [enabled, setEnabledState] = useState(true);
  const [volume, setVolumeState] = useState(DEFAULT_VOLUME);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  useEffect(() => {
    // Cuelume's delegated listeners survive route changes and dynamically
    // mounted controls, so this only needs to run once for the document.
    bind();
    queueMicrotask(() => {
      setEnabledState(readStoredEnabled());
      setVolumeState(readStoredVolume());
      setPreferencesLoaded(true);
    });
  }, []);

  useEffect(() => {
    if (!preferencesLoaded) return;
    setEnabled(enabled);
    // Storage may be unavailable (private browsing, WebView); the preference
    // still applies for the current session.
    writeStorage(ENABLED_STORAGE_KEY, String(enabled));
  }, [enabled, preferencesLoaded]);

  useEffect(() => {
    if (!preferencesLoaded) return;
    setVolume(volume);
    writeStorage(VOLUME_STORAGE_KEY, String(volume));
  }, [volume, preferencesLoaded]);

  return (
    <CuelumeContext.Provider
      value={{
        enabled,
        setEnabledPreference: setEnabledState,
      }}
    >
      <CuelumeNavigationCue />
      {children}
    </CuelumeContext.Provider>
  );
}

export function useCuelume() {
  const context = useContext(CuelumeContext);
  if (!context) throw new Error("useCuelume must be used inside CuelumeProvider");
  return context;
}

export function CuelumeToggle() {
  const { enabled, setEnabledPreference } = useCuelume();

  return (
    <button
      type="button"
      onClick={() => setEnabledPreference(!enabled)}
      data-icon-btn
      data-cuelume-toggle="toggle"
      // A stable name with aria-pressed as the one state channel: a label that
      // flips as well would read as the opposite of the pressed state.
      aria-label="Interface sounds"
      aria-pressed={enabled}
      title={enabled ? "Interface sounds on: click to mute (sound previews are not affected)" : "Interface sounds off: click to turn on"}
      // `bg-transparent` is a class, not an inline style: an inline ground
      // outranks `motion-reduce:active:bg-surface-hover` and left reduced-
      // motion users with no press feedback (§12.10).
      className="tlsr-focus bg-transparent rounded-[var(--ls-radius-round)] transition-[scale,background-color] duration-[var(--motion-duration-fast)] ease-[var(--motion-ease-standard)] motion-safe:active:scale-[0.97] motion-reduce:active:bg-surface-hover"
      style={{
        width: 44,
        height: 44,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--tls-nav-ink-muted)",
        border: "none",
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      {enabled ? (
        <Volume2 style={{ width: 18, height: 18 }} aria-hidden="true" />
      ) : (
        <VolumeX style={{ width: 18, height: 18 }} aria-hidden="true" />
      )}
    </button>
  );
}
